import { useAuth } from '@/components/auth/authProvider';
import { isPalmettoFinanceUser } from '@/components/auth/RequirePalmettoFinanceUser';
import FormSection from '@/components/Forms/FormSection';
import { FlagBox } from '@/components/QueueFlags/FlagBox';
import usePermissions from '@/hooks/usePermissions';
import { Box, FormikTextInput, SimulatedEventPayloadType } from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';

import {
  HVACAccessoryItem,
  HVACAccessoryLabels,
  HVACAccessoryType,
  HVACAirFilterTypeLabels,
  HVACZoningItem,
  RequirementFlagSettingsType,
  UserPermissions,
  ZoningType,
} from 'types';

interface AccessoriesSectionProps {
  systemIndex: number;
  isPackageEditable?: boolean;
}

const dataKey = 'accessories' as const;
const label = 'Accessories' as const;

export const InstallPackageAccessoriesSection = ({
  systemIndex,
  isPackageEditable = true,
}: AccessoriesSectionProps) => {
  const permissions = usePermissions();
  const { claims } = useAuth();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue } = useFormikContext<any>();

  return (
    <FormSection
      title={label}
      description={
        <Box gap="sm">
          <Box>
            Accessories added during the sales stage are listed here and eligible to be covered by the Palmetto
            Comfort Plan.
          </Box>
          <Box>
            If any accessories installed are not listed here, an updated system design and contract may be required
            to be re-signed. This can also lead to a higher monthly payment.
          </Box>
        </Box>
      }
      baseStyle={{
        borderWidth: '0',
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}[${dataKey}].items`}
        render={() => {
          return (
            <Box gap="lg">
              <Box direction={{ base: 'column', tablet: 'row' }} gap="md"></Box>
              {values?.systems[systemIndex]?.[dataKey]?.items
                ?.filter((item: HVACAccessoryItem | HVACZoningItem) => item.type !== HVACAccessoryType.zoning)
                .map?.(
                  (
                    accessoryItem: (HVACAccessoryItem | HVACZoningItem) & {
                      isSerialNumberRequired?: boolean;
                    },
                    index: number,
                  ) => {
                    return (
                      <Box key={`system-${systemIndex}-${dataKey}-${accessoryItem.type}-${index}`} childGap="md">
                        <Box direction="row" gap="lg" alignItems="baseline">
                          <Box direction="column" gap="sm" width={{ base: '60', tablet: '40' }}>
                            <Box fontSize="md" fontWeight="medium">
                              {dataKey === 'accessories'
                                ? HVACAccessoryLabels[accessoryItem.type as HVACAccessoryType]
                                : 'Zoning'}
                            </Box>
                            {accessoryItem.type === HVACAccessoryType.airFilter && accessoryItem.airFilterType ? (
                              <Box gap="xs" direction="column" fontSize="sm">
                                <Box>Type: {HVACAirFilterTypeLabels[accessoryItem.airFilterType]}</Box>
                              </Box>
                            ) : null}
                            <Box childGap="sm"></Box>
                          </Box>
                          {accessoryItem.type !== HVACAccessoryType.zoning ? (
                            <Box childGap="md" width="100%" direction="column">
                              <Field
                                label={'Manufacturer'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                component={FormikTextInput}
                                isRequired={accessoryItem.type !== ZoningType.bypassDamper}
                                isDisabled={!isPackageEditable}
                              />
                              <Field
                                label={accessoryItem.type === HVACAccessoryType.airFilter ? 'Model/Size' : 'Model'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                component={FormikTextInput}
                                isRequired={accessoryItem.type !== ZoningType.bypassDamper}
                                isDisabled={!isPackageEditable}
                              />
                              <FieldArray
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers`}
                                render={() => {
                                  const serialNumbers = values?.systems?.[systemIndex]?.[dataKey]?.items?.[index]
                                    ?.serialNumbers ?? [''];
                                  return serialNumbers.map((_serial: string, serialIndex: number) => {
                                    return (
                                      <Box padding="0 0 sm 0" key={serialIndex}>
                                        <Field
                                          placeholder={'Serial Number'}
                                          name={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.${serialIndex}`}
                                          id={`systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers.${serialIndex}`}
                                          component={FormikTextInput}
                                          width="100"
                                          label={`Serial Number${accessoryItem.isSerialNumberRequired ? ' *' : ''}`}
                                          isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                          onChange={(event: SimulatedEventPayloadType) => {
                                            const serialNumbers = [
                                              ...(values.systems[systemIndex][dataKey]?.items[index]
                                                ?.serialNumbers || []),
                                            ];
                                            serialNumbers[serialIndex] = event?.target?.value.trim();
                                            setFieldValue(
                                              `systems.${systemIndex}[${dataKey}].items.${index}.serialNumbers`,
                                              serialNumbers,
                                            );
                                          }}
                                        />
                                      </Box>
                                    );
                                  });
                                }}
                              />
                            </Box>
                          ) : (
                            <Box childGap="md" width="100%" direction="column">
                              <Field
                                label={'Number of Zones'}
                                type="number"
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                                component={FormikTextInput}
                                width="xl"
                                min={1}
                                isDisabled={!isFinCoUser || !isPackageEditable}
                                onChange={(event: SimulatedEventPayloadType) => {
                                  let value =
                                    event?.target?.value?.trim() === '' ? 1 : parseInt(event?.target?.value, 10);
                                  if (isNaN(value) || value < 1) {
                                    value = 1;
                                  }
                                  setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.quantity`, value);
                                }}
                                onBlur={(event: SimulatedEventPayloadType) => {
                                  if (event?.target?.value?.trim() === '') {
                                    setFieldValue(`systems.${systemIndex}[${dataKey}].items.${index}.quantity`, 1);
                                  }
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    );
                  },
                )}

              <FlagBox
                baseCategory="equipment"
                dataKey={`Installed ${label} Issue ${systemIndex + 1}`}
                flagType={RequirementFlagSettingsType.accessoriesHVAC}
              />
            </Box>
          );
        }}
      />
    </FormSection>
  );
};
